import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { User, Phone, Mail, MapPin, Building2 } from "lucide-react";
import { ContactButtons } from "@/components/orders/ContactButtons";
import { OrderDetailData } from "@/types/orders";
import { getCustomerDisplayName } from "@/lib/formatters";

interface OrderCustomerCardProps {
  customer: OrderDetailData["customers"];
  orderId: string;
  orderNumber: string;
}

export function OrderCustomerCard({ customer, orderId, orderNumber }: OrderCustomerCardProps) {
  if (!customer) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            Asiakas
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Ei asiakastietoja</p>
        </CardContent>
      </Card>
    );
  }

  // Use centralized getCustomerDisplayName from formatters

  const getFullAddress = () => {
    const parts = [
      customer.address,
      customer.address_line2,
      `${customer.postal_code} ${customer.city}`,
    ].filter(Boolean);
    return parts.join(", ");
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          Asiakas
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-3">
          {customer.company_name && (
            <div className="flex items-start gap-3">
              <Building2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <p className="font-medium">{customer.company_name}</p>
                {customer.contact_person && (
                  <p className="text-sm text-muted-foreground">
                    {customer.contact_person}
                  </p>
                )}
              </div>
            </div>
          )}

          {!customer.company_name && (customer.first_name || customer.last_name) && (
            <div className="flex items-start gap-3">
              <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <p className="font-medium">
                {customer.first_name} {customer.last_name}
              </p>
            </div>
          )}

          {customer.phone && (
            <div className="flex items-start gap-3">
              <Phone className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <a
                href={`tel:${customer.phone}`}
                className="text-sm hover:underline"
              >
                {customer.phone}
              </a>
            </div>
          )}

          {customer.email && (
            <div className="flex items-start gap-3">
              <Mail className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <a
                href={`mailto:${customer.email}`}
                className="text-sm hover:underline break-all"
              >
                {customer.email}
              </a>
            </div>
          )}

          {customer.address && (
            <div className="flex items-start gap-3">
              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <p className="text-sm">{getFullAddress()}</p>
            </div>
          )}
        </div>

        <div className="pt-2">
          <ContactButtons
            phone={customer.phone}
            email={customer.email}
            customerName={getCustomerDisplayName(customer)}
            orderId={orderId}
            customerId={customer.id}
            orderNumber={orderNumber}
          />
        </div>
      </CardContent>
    </Card>
  );
}
